<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import FileInput from '@uiKit/elements/FileInput.vue'
import FileRow from './FileRow.vue'

// The one upload field: a dashed drop zone (drag-and-drop or click) that turns
// into a FileRow once a file is picked. Every upload in the app uses this so
// the empty and picked states look the same everywhere; each caller only
// sets its copy, accepted types and size limit.

interface Props {
  modelValue: File | null
  /** "Drag and drop or click to upload statement". */
  prompt?: string
  /** MIME types accepted, e.g. ['application/pdf', 'image/jpeg']. */
  acceptTypes?: string[]
  /** Human list for the hint and the error, e.g. "PDF, JPEG, or PNG". */
  acceptLabel?: string
  maxSizeMb?: number
  invalid?: boolean
  disabled?: boolean
  /**
   * `true` → Preview emits `preview` for the caller's own viewer.
   * `false` (default) → Preview opens the file in a new tab.
   */
  previewInApp?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  prompt: 'Drag and drop or click to upload',
  acceptTypes: () => ['application/pdf', 'image/jpeg', 'image/png'],
  acceptLabel: 'PDF, JPEG, or PNG',
  maxSizeMb: 10,
  invalid: false,
  disabled: false,
  previewInApp: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: File | null): void
  (e: 'preview', file: File): void
}>()

const BYTES_PER_KB = 1024
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB
const ONE_MINUTE_MS = 60_000
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS
// A blob tab needs its URL alive long enough to load, then it is released.
const PREVIEW_URL_TTL_MS = 60_000

const MIME_EXTENSIONS: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/jpg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
}

const MIME_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPEG',
  'image/jpg': 'JPEG',
  'image/png': 'PNG',
}

const isDragging = ref(false)
const fileError = ref('')
const pickedAt = ref<number | null>(null)
const now = ref(Date.now())
let clock: ReturnType<typeof setInterval> | null = null

const acceptAttribute = computed(() =>
  [...new Set(props.acceptTypes.flatMap((type) => MIME_EXTENSIONS[type] ?? [type]))].join(','),
)

const errorMessage = computed(
  () => `Please upload a ${props.acceptLabel} file up to ${props.maxSizeMb} MB.`,
)

const formatSize = (bytes: number): string => {
  const megabytes = bytes / BYTES_PER_MB
  return megabytes < 1 ? `${(bytes / BYTES_PER_KB).toFixed(1)} KB` : `${megabytes.toFixed(1)} MB`
}

const fileKind = (file: File): string =>
  MIME_LABELS[file.type] ?? file.name.split('.').pop()?.toUpperCase() ?? 'File'

const pickedLabel = computed(() => {
  if (!pickedAt.value) return 'Just now'
  const elapsed = now.value - pickedAt.value
  if (elapsed < ONE_MINUTE_MS) return 'Just now'
  if (elapsed < ONE_HOUR_MS) return `${Math.floor(elapsed / ONE_MINUTE_MS)} min ago`
  return `${Math.floor(elapsed / ONE_HOUR_MS)} h ago`
})

const metaLine = computed(() =>
  props.modelValue
    ? `${fileKind(props.modelValue)} · ${formatSize(props.modelValue.size)} · ${pickedLabel.value}`
    : '',
)

// Keep "2 min ago" honest while the field stays on screen.
watch(
  () => props.modelValue,
  (file) => {
    pickedAt.value = file ? Date.now() : null
    if (clock) clearInterval(clock)
    clock = file ? setInterval(() => (now.value = Date.now()), ONE_MINUTE_MS) : null
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  if (clock) clearInterval(clock)
})

const accept = (file: File | null | undefined) => {
  if (!file) return
  if (!props.acceptTypes.includes(file.type) || file.size > props.maxSizeMb * BYTES_PER_MB) {
    fileError.value = errorMessage.value
    return
  }
  fileError.value = ''
  emit('update:modelValue', file)
}

const handlePick = (pick: () => void) => {
  if (!props.disabled) pick()
}

const handleDragOver = () => {
  if (!props.disabled) isDragging.value = true
}

const handleDrop = (event: DragEvent) => {
  isDragging.value = false
  if (!props.disabled) accept(event.dataTransfer?.files?.[0])
}

// `clear()` resets the native input (so re-picking the same file still fires
// change); its null goes through `accept`, which ignores empty picks — so the
// removal itself is emitted here.
const handleRemove = (clear: () => void) => {
  fileError.value = ''
  clear()
  emit('update:modelValue', null)
}

const handlePreview = () => {
  const file = props.modelValue
  if (!file) return
  if (props.previewInApp) {
    emit('preview', file)
    return
  }
  const url = URL.createObjectURL(file)
  window.open(url, '_blank', 'noopener')
  setTimeout(() => URL.revokeObjectURL(url), PREVIEW_URL_TTL_MS)
}
</script>

<template>
  <Box direction="col" gap="2" class="file-upload">
    <FileInput :model-value="modelValue" :accept="acceptAttribute" @update:model-value="accept">
      <template #default="{ pick, clear, file }">
        <FileRow
          v-if="file"
          :name="file.name"
          :meta="metaLine"
          removable
          :disabled="disabled"
          @preview="handlePreview"
          @remove="handleRemove(clear)"
        />

        <Box
          v-else
          is="button"
          type="button"
          direction="col"
          align="center"
          justify="center"
          gap="2"
          class="file-upload__zone"
          :class="{
            'file-upload__zone--active': isDragging,
            'file-upload__zone--invalid': invalid || Boolean(fileError),
          }"
          :disabled="disabled"
          @click="handlePick(pick)"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleDrop"
        >
          <PiIcon name="upload" class="file-upload__icon" />
          <Text is="span" type="heading" weight="semibold" align="center">{{ prompt }}</Text>
          <Text is="span" type="body" color="var(--p-surface-400)" align="center">
            {{ acceptLabel }} &nbsp;·&nbsp; Max {{ maxSizeMb }} MB
          </Text>
        </Box>
      </template>
    </FileInput>

    <Box v-if="fileError" align="center" gap="1" role="alert">
      <PiIcon name="exclamation-circle" class="file-upload__error-icon" />
      <Text is="span" type="caption" color="danger">{{ fileError }}</Text>
    </Box>
  </Box>
</template>

<style scoped>
.file-upload {
  width: 100%;
}

.file-upload__zone {
  width: 100%;
  min-height: 150px;
  /* Figma (Add Funds / bank statement): 34px top and bottom. */
  padding: 34px 16px;
  border: 1.5px dashed var(--p-primary-300);
  border-radius: 12px;
  background: var(--p-primary-50);
  box-sizing: border-box;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
}

.file-upload__zone:hover,
.file-upload__zone--active {
  border-color: var(--p-primary-500);
  background: var(--p-primary-100);
}

.file-upload__zone:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.file-upload__zone--invalid {
  border-color: var(--p-danger-600);
}

.file-upload__zone:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.file-upload__icon {
  font-size: 36px;
  color: var(--p-primary-600);
}

.file-upload__error-icon {
  font-size: 12px;
  color: var(--p-danger-600);
}
</style>
